import { Group, Badge, ActionIcon, Button, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { type CaseFilters, patientsService } from "../../services";
import {
  useFacilities,
  usePathologists,
  usePhysicians,
  useAccounts,
  useLabs,
  useSurgeryCenters,
} from "../../hooks/useReferenceData";
import { useCaseStatusesForFiltering } from "../../hooks/useAdmin";
import { workflowsService } from "../../services";
import { proceduresService } from "../../services/admin/procedures.service";
import { flagDefinitionsService } from "../../services/flagDefinitionsService";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";

function formatCanonicalFilterDate(value: unknown): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "Invalid date";
}

interface ActiveFiltersProps {
  filters: CaseFilters;
  onFiltersChange: (filters: CaseFilters) => void;
  onClearAll: () => void;
  showRelationshipFilters?: boolean;
  prefixOptions?: string[];
}

interface ActiveFilter {
  key: keyof CaseFilters;
  label: string;
  value: string;
  itemId?: string; // For array filters, the specific ID to remove
}

export default function ActiveFilters({
  filters,
  onFiltersChange,
  onClearAll,
  showRelationshipFilters = false,
  prefixOptions,
}: ActiveFiltersProps) {
  const { data: facilities = [] } = useFacilities();
  const { data: practices = [] } = useAccounts({
    enabled: showRelationshipFilters,
  });
  const { data: labs = [] } = useLabs({ enabled: showRelationshipFilters });
  const { data: surgeryCenters = [] } = useSurgeryCenters({
    enabled: showRelationshipFilters,
  });
  const { data: pathologists = [] } = usePathologists();
  const { data: physicians = [] } = usePhysicians();
  const { data: workflows = [] } = useQuery({
    queryKey: ["workflows"],
    queryFn: workflowsService.getAll,
    staleTime: 1000 * 60 * 60,
  });
  const { data: caseStatuses = [] } = useCaseStatusesForFiltering();
  const { data: procedures = [] } = useQuery({
    queryKey: ["procedures"],
    queryFn: proceduresService.getAll,
    staleTime: 1000 * 60 * 60,
  });
  const { data: activeFlags = [] } = useQuery({
    queryKey: ["activeFlagDefinitions"],
    queryFn: () => flagDefinitionsService.getActive(),
    staleTime: 1000 * 60 * 60,
    enabled: !!filters.flagDefinitionIds?.length,
  });

  // Fetch selected patients
  const { data: selectedPatients = [] } = useQuery({
    queryKey: ["patients-selected", filters.patientIds],
    queryFn: async () => {
      if (!filters.patientIds?.length) return [];
      const patients = await Promise.all(
        filters.patientIds.map((id) =>
          patientsService.getById(id).catch(() => null)
        )
      );
      return patients.filter(Boolean);
    },
    enabled: !!filters.patientIds?.length,
  });

  const activeFilters: ActiveFilter[] = [];

  // Build array of active filters with human-readable labels

  if (prefixOptions && filters.prefixKeys?.length) {
    filters.prefixKeys.forEach((prefixKey) => {
      const label =
        prefixKey === "__OTHER__" ? "Other / nonstandard number" : prefixKey;
      activeFilters.push({
        key: "prefixKeys",
        label: "Prefix",
        value: prefixOptions.includes(prefixKey)
          ? label
          : `${label} (unavailable)`,
        itemId: prefixKey,
      });
    });
  }

  // Facilities (array)
  if (filters.facilityIds?.length) {
    filters.facilityIds.forEach((facilityId) => {
      const facility = facilities.find((f) => f.id === facilityId);
      activeFilters.push({
        key: "facilityIds",
        label: "Facility",
        value: facility?.name || facilityId,
        itemId: facilityId,
      });
    });
  }

  if (showRelationshipFilters && filters.sendingPracticeId?.length) {
    filters.sendingPracticeId.forEach((practiceId) => {
      const practice = practices.find((option) => option.value === practiceId);
      activeFilters.push({
        key: "sendingPracticeId",
        label: "Practice",
        value: practice?.label || practiceId,
        itemId: practiceId,
      });
    });
  }

  if (showRelationshipFilters && filters.submittingLabId?.length) {
    filters.submittingLabId.forEach((labId) => {
      const lab = labs.find((option) => option.value === labId);
      activeFilters.push({
        key: "submittingLabId",
        label: "Sending Lab",
        value: lab?.label || labId,
        itemId: labId,
      });
    });
  }

  if (showRelationshipFilters && filters.surgeryCenterLabId?.length) {
    filters.surgeryCenterLabId.forEach((centerId) => {
      const center = surgeryCenters.find((option) => option.value === centerId);
      activeFilters.push({
        key: "surgeryCenterLabId",
        label: "Surgical Center",
        value: center?.label || centerId,
        itemId: centerId,
      });
    });
  }

  // Workflows (array)
  if (filters.workflowIds?.length) {
    filters.workflowIds.forEach((workflowId) => {
      const workflow = workflows.find((w) => w.id === workflowId);
      const workflowName =
        workflow?.name ||
        (workflow?.accessioningType
          ? workflow.accessioningType
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c: string) => c.toUpperCase())
          : workflowId);
      activeFilters.push({
        key: "workflowIds",
        label: "Workflow",
        value: workflowName,
        itemId: workflowId,
      });
    });
  }

  // Statuses (array)
  if (filters.currentStatusIds?.length) {
    filters.currentStatusIds.forEach((statusId) => {
      const status = caseStatuses.find((s) => s.id === statusId);
      activeFilters.push({
        key: "currentStatusIds",
        label: "Status",
        value: status?.name || statusId,
        itemId: statusId,
      });
    });
  }

  // Pathologists (array)
  if (filters.pathologistIds?.length) {
    filters.pathologistIds.forEach((pathologistId) => {
      const pathologist = pathologists.find((p) => p.id === pathologistId);
      activeFilters.push({
        key: "pathologistIds",
        label: "Assigned Pathologist",
        value: pathologist?.name || pathologistId,
        itemId: pathologistId,
      });
    });
  }

  // Procedures (array)
  if (filters.procedureIds?.length) {
    filters.procedureIds.forEach((procedureId) => {
      const procedure = procedures.find((p) => p.id === procedureId);
      activeFilters.push({
        key: "procedureIds",
        label: "Procedure",
        value: procedure?.name || procedureId,
        itemId: procedureId,
      });
    });
  }

  // Patients (array)
  if (filters.patientIds?.length) {
    filters.patientIds.forEach((patientId) => {
      const patient = selectedPatients.find((p) => p?.id === patientId);
      const patientName = patient
        ? `${patient.lastName}, ${patient.firstName}`
        : "Loading...";
      activeFilters.push({
        key: "patientIds",
        label: "Patient",
        value: patientName,
        itemId: patientId,
      });
    });
  }

  // Search
  if (filters.search) {
    activeFilters.push({
      key: "search",
      label: "Search",
      value: filters.search,
    });
  }

  if (filters.dateFrom) {
    activeFilters.push({
      key: "dateFrom",
      label: "From Date",
      value: formatCanonicalFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    activeFilters.push({
      key: "dateTo",
      label: "To Date",
      value: formatCanonicalFilterDate(filters.dateTo),
    });
  }

  if (filters.physicianId) {
    const physician = physicians.find((p) => p.value === filters.physicianId);
    activeFilters.push({
      key: "physicianId",
      label: "Ordering Provider",
      value: physician?.label || filters.physicianId,
    });
  }

  if (filters.dateCollected) {
    activeFilters.push({
      key: "dateCollected",
      label: "Collection Date",
      value: formatCanonicalFilterDate(filters.dateCollected),
    });
  }

  if (filters.flagDefinitionIds?.length) {
    filters.flagDefinitionIds.forEach((flagId) => {
      const flag = activeFlags.find((definition) => definition.id === flagId);
      activeFilters.push({
        key: "flagDefinitionIds",
        label: "Flag",
        value: flag?.name || flagId,
        itemId: flagId,
      });
    });
  }

  if (filters.isRush) {
    activeFilters.push({
      key: "isRush",
      label: "Priority",
      value: "RUSH Only",
    });
  }

  if (filters.isHighPriority) {
    activeFilters.push({
      key: "isHighPriority",
      label: "Priority",
      value: "High Priority Only",
    });
  }

  if (filters.isBlocked) {
    activeFilters.push({
      key: "isBlocked",
      label: "Status",
      value: "Blocked Only",
    });
  }

  if (filters.isAdditionalStain) {
    activeFilters.push({
      key: "isAdditionalStain",
      label: "Type",
      value: "Additional Stains",
    });
  }

  if (filters.currentStep) {
    activeFilters.push({
      key: "currentStep",
      label: "Step",
      value:
        filters.currentStep.charAt(0).toUpperCase() +
        filters.currentStep.slice(1),
    });
  }

  if (filters.caseType) {
    activeFilters.push({
      key: "caseType",
      label: "Case Type",
      value: filters.caseType,
    });
  }

  if (filters.tatOverdue) {
    activeFilters.push({
      key: "tatOverdue",
      label: "TAT",
      value: "Overdue",
    });
  }

  if (filters.tatMinDays != null) {
    activeFilters.push({
      key: "tatMinDays",
      label: "TAT",
      value: `${filters.tatMinDays}+ days`,
    });
  }

  if (filters.hasPendingTests === true) {
    activeFilters.push({
      key: "hasPendingTests",
      label: "Pending Tests",
      value: "Has Pending",
    });
  } else if (filters.hasPendingTests === false) {
    activeFilters.push({
      key: "hasPendingTests",
      label: "Pending Tests",
      value: "No Pending",
    });
  }

  // Handle removing a filter - for arrays, remove just that item
  const handleRemoveFilter = (filter: ActiveFilter) => {
    const { key, itemId } = filter;

    if (itemId) {
      // This is an array filter, remove just this item
      const currentArray = filters[key] as string[] | undefined;
      if (currentArray) {
        const newArray = currentArray.filter((id) => id !== itemId);
        onFiltersChange({
          ...filters,
          [key]: newArray.length > 0 ? newArray : undefined,
        });
      }
    } else {
      // This is a non-array filter, clear it entirely
      onFiltersChange({
        ...filters,
        [key]: undefined,
      });
    }
  };

  // Don't show anything if no filters are active
  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Group gap="xs" align="center">
      <Text size="sm" fw={500} c="dimmed">
        Active Filters:
      </Text>
      {activeFilters.map((filter, index) => (
        <Badge
          key={`${filter.key}-${filter.itemId || index}`}
          variant="light"
          size="xs"
          pr={2}
          rightSection={
            <ActionIcon
              aria-label={`Remove ${filter.value} filter`}
              size="xs"
              color="blue"
              radius="xl"
              variant="transparent"
              onClick={() => handleRemoveFilter(filter)}
            >
              <IconX size={10} />
            </ActionIcon>
          }
        >
          {filter.label}: {filter.value}
        </Badge>
      ))}
      <Button variant="subtle" size="compact-xs" onClick={onClearAll}>
        Clear All
      </Button>
    </Group>
  );
}
